import type { FC } from 'react';
import React, { useContext, useState } from 'react';
import { defineMessages, useIntl } from 'react-intl-next';

import { Manager, Popper, Reference } from '@atlaskit/popper';
import { IconButton } from '@atlaskit/button/new';
import Tooltip from '@atlaskit/tooltip';

import type { AIEventsInstrumentationConfig } from '@atlassian/ai-analytics';
import { useAIEventsInstrumentation } from '@atlassian/ai-analytics';

import type { ActiveAudioContent } from '@confluence/audio';
import { AudioBriefingPopupContent, useAudioBriefing } from '@confluence/audio';
import { useSessionData } from '@confluence/session-data';
import { ExperienceTrackerContext, AI_AUDIO_BRIEFING } from '@confluence/experience-tracker';
import { useAIRovoButtonEnablement } from '@confluence/ai-floating-context-menu/entry-points/useAIRovoButtonEnablement';

import { BriefingIcon } from '../assets/BriefingIcon';

const i18n = defineMessages({
	listen: {
		id: 'object-sidebar-components.audio.button.tooltip',
		defaultMessage: 'Listen to briefing',
		description:
			'Message in tooltip which tells user that this button is tied to listen to a briefing',
	},
});

type AudioButtonProps = {
	contentId: string;
	contentType?: string | null;
	spaceKey?: string;
	contentTitle?: string | null;
};

const aiAnalyticsConfig: AIEventsInstrumentationConfig = {
	product: 'confluence',
	subproduct: 'confluence',
	source: 'page',
	aiFeatureName: 'aiAudioBriefing',
	proactiveGeneratedAI: 0,
	userGeneratedAI: 1,
};

const AudioButtonComponent: FC<AudioButtonProps> = ({
	contentId,
	contentType,
	spaceKey,
	contentTitle,
}) => {
	const { formatMessage, locale } = useIntl();

	const [isPopupOpen, setIsPopupOpen] = useState<boolean>(false);
	const [isPopupMinimized, setIsPopupMinimized] = useState<boolean>(false);

	const [{ activeContent: prevActiveContent, hasFetched, streamEnded, error }, { initialize }] =
		useAudioBriefing();

	const { cloudId, activationId } = useSessionData();

	const isRovoButtonEnabled = useAIRovoButtonEnablement();

	const experienceTracker = useContext(ExperienceTrackerContext);

	const { trackAIResultAction, trackAIInteractionDismiss } =
		useAIEventsInstrumentation(aiAnalyticsConfig);

	const activeContent: ActiveAudioContent = {
		id: contentId,
		// Should we just fail if there is no contentType?
		contentType: contentType ?? 'page',
		spaceKey,
		contentTitle,
	};

	const handleOpen = () => {
		setIsPopupOpen(true);
	};

	const handleClose = () => {
		setIsPopupOpen(false);
	};

	const handleCloseWithAnalytics = () => {
		if (hasFetched && !error) {
			if (streamEnded) {
				trackAIResultAction('audioClosed');
			} else {
				experienceTracker.abort({
					name: AI_AUDIO_BRIEFING,
					reason: 'briefing popup was closed',
				});
				trackAIInteractionDismiss();
			}
		}
		handleClose();
	};

	const handleAudioButtonClick = () => {
		if (isPopupOpen) {
			if (isPopupMinimized) {
				setIsPopupMinimized(false);
				return;
			}
			handleCloseWithAnalytics();
		} else {
			handleOpen();
			initialize({
				activeContent,
				cloudId,
				activationId,
				locale,
			});
		}
	};

	// Close popper if the contentId changes meaning the user has navigated to different content
	if (isPopupOpen && prevActiveContent?.id !== contentId) {
		handleCloseWithAnalytics();
	}

	return (
		<Manager>
			<Reference>
				{({ ref }) => (
					<Tooltip content={formatMessage(i18n.listen)} position="left" hideTooltipOnClick>
						<IconButton
							label={formatMessage(i18n.listen)}
							appearance="subtle"
							icon={BriefingIcon}
							shape={isRovoButtonEnabled ? 'default' : 'circle'}
							onClick={handleAudioButtonClick}
							testId="audio-button"
							ref={ref}
						/>
					</Tooltip>
				)}
			</Reference>
			{isPopupOpen && (
				<Popper placement="left-end" offset={[0, 16]}>
					{({ ref, style }) => (
						// eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop -- Ignored via go/DSP-18766
						<div ref={ref} style={style}>
							<AudioBriefingPopupContent
								closePopup={handleClose}
								isPopupMinimized={isPopupMinimized}
								setIsPopupMinimized={setIsPopupMinimized}
							/>
						</div>
					)}
				</Popper>
			)}
		</Manager>
	);
};

export const AudioButton: FC<AudioButtonProps> = ({
	contentId,
	contentType,
	spaceKey,
	contentTitle,
}) => {
	return (
		<AudioButtonComponent
			contentId={contentId}
			contentType={contentType}
			spaceKey={spaceKey}
			contentTitle={contentTitle}
		/>
	);
};
